import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import * as _ from 'lodash';
import * as Fuse from 'fuse.js';
import * as firebase from 'firebase';

import Backend from '../Layouts/Backend';
import DriversToolbar from './DriversToolbar';
import DriversTable from './DriversTable';
import DriverRow from './DriverRow';
import { deepEqual } from '../utils';
import driverFormPopup from './DriverFormPopup';
import workedDaysReportPopup from './workedDaysReport.popup';
import availableDaysReportPopup from './availableDaysReport.popup';

import snackbar from '../snackbar';

const INITIAL_STATE = {
  orderBy: {
    firstName: 'asc',
    surname: 'asc',
  },
  searchMode: false,
  searchTerm: '',
  isSearching: false,
  foundDrivers: [],
  selectedDriverIds: [],
};

const FUSE_OPTIONS = {
  // TEMP: id: '$key',
  shouldSort: true,
  // includeScore: true,
  // includeMatches: true,

  threshold: 0.35,
  location: 0,
  distance: 200,

  maxPatternLength: 32,
  minMatchCharLength: 1,
  keys: [
    {
      name: 'fullName',
      weight: 1,
    },
    {
      name: 'nickname',
      weight: 0.7,
    },
    {
      name: 'homeAddress',
      weight: 0.35,
    },
  ],
};

class DriversPage extends Component {
  constructor(props) {
    super(props);
    this.state = INITIAL_STATE;
    this.onSearchInput = this.onSearchInput.bind(this);
    this.onAddClick = this.onAddClick.bind(this);
    this.onToggleDriverSelection = this.onToggleDriverSelection.bind(this);
    this.onToggleAllSelection = this.onToggleAllSelection.bind(this);
    this.onWorkedDaysReportClick = this.onWorkedDaysReportClick.bind(this);
    this.onAvailableDaysReportClick = this.onAvailableDaysReportClick.bind(this);
    this.searchDrivers = _.debounce(this.searchDrivers.bind(this), 200);
    this.onRecalculateClick = this.onRecalculateClick.bind(this);
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !deepEqual(this.props, nextProps) || !deepEqual(this.state, nextState);
  }

  onSearchInput(term) {
    const searchState = term.length > 0
      ? {
        searchMode: true,
        isSearching: true,
        searchTerm: term,
      }
      : {
        searchMode: false,
        isSearching: false,
        searchTerm: term,
      };

    if (term.length < 3) {
      this.setState({
        ...searchState,
        foundDrivers: [],
      });
      return;
    }

    this.setState(searchState);
    this.searchDrivers();
  }

  onAddClick() {
    driverFormPopup();
  }

  onToggleDriverSelection(id) {
    if (this.state.selectedDriverIds.includes(id)) {
      this.setState({
        selectedDriverIds: this.state
          .selectedDriverIds.filter(v => v !== id),
      });
    } else {
      this.setState({
        selectedDriverIds: [
          ...this.state.selectedDriverIds,
          id,
        ],
      });
    }
  }

  onToggleAllSelection() {
    if (this.state.selectedDriverIds.length > 0) {
      this.setState({
        selectedDriverIds: [],
      });
    } else {
      this.setState({
        selectedDriverIds: this.getDrivers().map(d => d.$key),
      });
    }
  }

  onWorkedDaysReportClick() {
    const { selectedDriverIds: driverIds } = this.state;
    workedDaysReportPopup(driverIds);
  }

  onAvailableDaysReportClick() {
    const { selectedDriverIds: driverIds } = this.state;
    availableDaysReportPopup(driverIds);
  }

  onRecalculateClick() {
    const { selectedDriverIds: driverIds } = this.state;

    if (_.size(driverIds) < 1) {
      snackbar.show({
        text: 'Selecione os motoristas...',
      });
      return undefined;
    }

    firebase.database()
      .ref('/actions')
      .push({
        client: this.props.client,
        type: 'DRIVERS_LIFE_EVENTS_RECALCULATION',
        dispatchedAt: firebase.database.ServerValue.TIMESTAMP,
        payload: { driverIds },
        uid: this.props.uid,
      })
      // eslint-disable-next-line no-console
      .then((snap) => { console.log('Recalculation triggered: ', snap.key); })
      // eslint-disable-next-line no-console
      .catch((err) => { console.error('Recalculation error: ', err); });

    snackbar.show({
      text: 'Recálculo iniciado... (1-2 minutos)',
    });
    return undefined;
  }

  getPureDrivers() {
    return _.map(this.props.drivers, (driver, id) => ({
      $key: id,
      id,
      fullName: `${driver.firstName} ${driver.surname}`,
      ...driver,
    }));
  }

  getSortedDrivers() {
    const propsNames = [];
    const propsOrders = [];

    _.map(this.state.orderBy, (propOrder, propName) => {
      propsNames.push(propName);
      propsOrders.push(propOrder);
    });

    return _.orderBy(this.getPureDrivers(), propsNames, propsOrders);
  }

  getDrivers() {
    return (this.state.searchMode) ? this.state.foundDrivers : this.getSortedDrivers();
  }

  async searchDrivers() {
    const fuse = new Fuse(this.getPureDrivers(), FUSE_OPTIONS);
    this.setState({
      foundDrivers: fuse.search(this.state.searchTerm),
      isSearching: false,
    });
  }

  render() {
    const drivers = this.getDrivers();
    const rows = drivers.map(driver => (
      <DriverRow
        key={driver.$key}
        driver={driver}
        isSelected={this.state.selectedDriverIds.includes(driver.$key)}
        onToggleSelection={this.onToggleDriverSelection}
      />
    ));

    const countShown = rows.length;
    const countAll = Object.keys(this.props.drivers).length;

    return (
      <Backend>
        <div className="content-inner">
          <DriversToolbar
            count={countShown}
            searchTerm={this.state.searchTerm}
            isSearching={this.state.isSearching}
            onSearchInput={this.onSearchInput}
            onAddClick={this.onAddClick}
            onWorkedDaysReportClick={this.onWorkedDaysReportClick}
            onAvailableDaysReportClick={this.onAvailableDaysReportClick}
            onRecalculateClick={this.onRecalculateClick}
          />

          <DriversTable
            allCount={countAll}
            selectedCount={this.state.selectedDriverIds.length}
            onToggleSelection={this.onToggleAllSelection}
          >
            {rows}
          </DriversTable>
        </div>
      </Backend>
    );
  }
}

DriversPage.propTypes = {
  drivers: PropTypes.object.isRequired,
  client: PropTypes.string.isRequired,
  uid: PropTypes.string.isRequired,
};

const mapStateToProps = store => ({
  drivers: store.driverState.drivers || {},
  client: (store.authState.currentUser || {}).client || '',
  uid: (store.authState.currentUser || {}).uid || '',
});

export default connect(mapStateToProps, null)(DriversPage);



// WEBPACK FOOTER //
// ./src/Driver/DriversPage.js